// <!--/assets/regimens.js-->
// STS_REGIMENS_V2 — the "how to apply your routine" page, rebuilt on the
// current quiz engine (2026-08-21).
//
// The original page read `customerRoutine` from localStorage — a key only the
// retired Octane-era quiz ever wrote — so everyone who took the current quiz
// was silently bounced back to the quiz landing, and pre-go-live customers saw
// a routine the engine no longer prescribes. This version resolves the routine
// exactly the way the results page does, in the same priority order:
//
//   1. sessionStorage `slqResultsHandoff` — this tab just finished the quiz
//   2. localStorage  `slqSavedResults`    — a taker who saved their results
//      (either carries a widget token → live GET {EC2}/widget/regimen)
//   3. localStorage  `slqSavedRegimen`    — the 30-day cached copy the results
//      page writes for EVERY taker on a successful live render
//   4. nothing → the quiz landing, same as the page always did
//
// What renders is the old page's experience — AM / PM step cards with the
// per-slot application-instruction carousels (product_instruction_steps
// metaobjects), the product modal with add-to-cart, and the Complete/Starter
// toggle (now driven by the payload's own `tier` field). The markup keeps the
// original class names so quiz-results-main.css and regimens.css style it
// unchanged.

(async () => {
  'use strict';

  /* ------------------------------------------------------------------ */
  /* setup                                                               */
  /* ------------------------------------------------------------------ */

  var savedValuesEl = document.querySelector('[data-saved-values]');
  var savedValues = {};
  try { savedValues = JSON.parse(savedValuesEl ? savedValuesEl.textContent : '{}') || {}; } catch (e) { /* keep {} */ }

  var storefrontAccessToken = savedValues.storefrontAccessToken || '';
  var defaultImage = savedValues.defaultImage || '';
  var defaultProductImage = savedValues.defaultProductImage || '';
  var defaultAltText = 'Image';

  var regimensPage = document.querySelector('[data-regimens]');
  if (!regimensPage) return;
  var noProductsError = regimensPage.getAttribute('data-no-products') || 'No products in this routine yet.';
  var loader = document.querySelector('[data-loader]');
  var body = document.querySelector('body');
  var drawer = document.querySelector('[data-drawer="drawer-cart"]');

  /* The section can carry an override; above both sits snippets/sts-env.liquid,
     which resolves the sts.api_env / sts.api_base_* shop metafields once per
     page and publishes window.STS.env. With no metafield set this is the same
     production host it has always been, and dev is refused outright on the
     published theme. */
  var EC2_BASE = (function () {
    var attr = regimensPage.getAttribute('data-ec2-base');
    var E = window.STS && window.STS.env;
    var v = (E && E.get) ? E.get('apiBase', attr || 'https://api.skintype.pro')
                         : (attr || 'https://api.skintype.pro');
    return String(v).replace(/\/+$/, '');
  }());
  var QUIZ_URL = '/pages/skin-type-quiz';
  var RESULTS_URL = '/pages/skin-type-results';
  var GRAPHQL_URL = '/api/2024-01/graphql.json';

  var toggleLoader = function (on) {
    if (!loader) return;
    if (on) loader.classList.add('--active');
    else loader.remove();
  };

  /* ------------------------------------------------------------------ */
  /* routine resolution — mirrors sections/sts-liquid-results.liquid      */
  /* ------------------------------------------------------------------ */

  function readJSON(storage, key) {
    try { return JSON.parse(storage.getItem(key) || 'null'); } catch (e) { return null; }
  }

  /* Whose routine is this, and whose four letters go above it? One question,
     one answer. Each source carries BOTH a widget token and the type it vouches
     for, so the two are taken TOGETHER: the pair of independent `||` chains
     this replaces could take the steps from one blob and the "Built for your
     Baumann Skin Type" line from the other, which is a DIFFERENT result. That
     is the defect PR #216 fixed on the printed sheet, where a stale DSNT badge
     sat four inches above ORNW headings over ORNW products.

     And every source is TRIED, not just the first one holding a token. The
     handoff carries no timestamp (see the note at assets/sts-liquid-quiz.js),
     so nothing on it says which navigation it belongs to: a tab left open
     across a re-quiz in another tab still holds the older result, and its token
     401s once it is past its two hours. Stopping there sent a fully typed
     visitor to the quiz landing with a good saved result sitting untouched
     beside it. assets/sts-steps.js walks its whole chain for the same reason. */
  var deviceType = String(window.localStorage.getItem('customerSkinType') || '').toUpperCase();
  var sources = [
    readJSON(window.sessionStorage, 'slqResultsHandoff'),
    readJSON(window.localStorage, 'slqSavedResults')
  ].filter(function (s) { return s && s.widgetToken; });
  /* Before anything is fetched the only honest label is the first source's own
     claim, and the device's current type where it makes none — which the quiz
     rewrites on every completion. Never the other blob's. Once a source
     answers, fetchLiveRegimen() restates this from THAT source. */
  var skinType = String((sources[0] && sources[0].skinType) || deviceType).toUpperCase();

  /* Same rules as the results page: 30 days max, never an empty list, and a
   * cache written for a DIFFERENT skin type (shared device, or pre-re-quiz) is
   * someone else's routine — never render it. */
  var CACHE_MAX_AGE_MS = 30 * 24 * 60 * 60 * 1000;
  function readCachedRegimen() {
    var c = readJSON(window.localStorage, 'slqSavedRegimen');
    if (!c || !Array.isArray(c.steps) || !c.steps.length) return null;
    if (!c.cachedAt || (Date.now() - c.cachedAt) > CACHE_MAX_AGE_MS) return null;
    if (skinType && c.skinType && String(c.skinType).toUpperCase() !== skinType) return null;
    return c;
  }

  /* Sequential on purpose: the second token is only worth spending when the
     first has actually failed, and a typed visitor normally holds one source,
     so this is the same single request it has always been. Whichever source
     answers also names the type printed above its steps — that pairing is the
     whole contract. */
  async function fetchLiveRegimen() {
    for (var i = 0; i < sources.length; i++) {
      var src = sources[i];
      try {
        var res = await fetch(EC2_BASE + '/widget/regimen', {
          headers: { Authorization: 'Bearer ' + src.widgetToken }
        });
        if (!res.ok) continue;
        var data = await res.json();
        if (!data || !Array.isArray(data.steps) || !data.steps.length) continue;
        skinType = String(src.skinType || skinType || '').toUpperCase();
        return data.steps;
      } catch (e) { /* network error on this source: try the next one */ }
    }
    return null;
  }

  /* The staff simulator's routine fixture, ahead of every real source and
     inert without it (assets/sts-simulator.js; the accessor is
     STS.previewRoutine in assets/sts-state.js, which returns null unless this
     session asked for a fixture AND the theme is provably not the published
     one). Steps arrive in the widget/regimen shape, so everything below this
     line treats them exactly as it treats a live payload. */
  var previewRoutine = (window.STS && typeof window.STS.previewRoutine === 'function')
    ? await window.STS.previewRoutine() : null;
  if (previewRoutine && previewRoutine.skinType) skinType = String(previewRoutine.skinType).toUpperCase();

  var rawSteps = previewRoutine ? previewRoutine.steps : await fetchLiveRegimen();
  if (!rawSteps) {
    /* No token answered, so no source owns this page and the type claimed
       above is unbacked — hand the question to the device before the cache is
       gated on it. `customerSkinType` is rewritten by EVERY quiz completion, in
       any tab, while the handoff is per-tab and carries no timestamp, so a tab
       left open across a re-quiz would otherwise gate the cache on the type it
       was showing yesterday. Both directions of that are wrong: it refuses this
       visitor's own current cache and sends a fully typed person to the quiz
       landing, and it ADMITS the pre-re-quiz cache whenever the two stale blobs
       agree with each other — which is how a DSNT routine under a DSNT byline
       reaches a visitor whose <html data-sts-type> says ORNW. That is the
       two-types-on-one-page defect of PR #216, on this page. */
    if (deviceType) skinType = deviceType;
    var cached = readCachedRegimen();
    if (cached) {
      rawSteps = cached.steps;
      // The cache remembers whose routine it is even when the handoff and the
      // legacy customerSkinType key are long gone — keep the "Built for your
      // Baumann Skin Type" line alive on the cache-only path.
      if (!skinType && cached.skinType) skinType = String(cached.skinType).toUpperCase();
    }
  }
  if (!rawSteps || !rawSteps.length) {
    // Nothing to show for this visitor — same exit the page always had, except
    // that it now carries the staff simulator's parameters and preview_theme_id
    // through instead of dropping them. This page serves both /pages/regimens
    // and /pages/my-routine-steps, and dropping them threw a reviewer back to
    // qminus on the PUBLISHED theme, which reads as a broken simulator rather
    // than as a redirect. A real visitor carries none of them, so for them this
    // is byte-identical. STS.keepPreview lives in assets/sts-state.js.
    window.location.href = (window.STS && typeof window.STS.keepPreview === 'function')
      ? window.STS.keepPreview(QUIZ_URL) : QUIZ_URL;
    return;
  }

  /* ------------------------------------------------------------------ */
  /* results-page design language                                        */
  /* ------------------------------------------------------------------ */

  /* One ramp (spec 08 DS-02, §3.7): the type colours come from the
   * #sts-skin-types JSON that snippets/sts-head.liquid prints, the same values
   * assets/sts-tokens.css paints v4 pages with and the results page reads.
   * TYPE_PALETTE below is the FALLBACK for a page without that JSON. It used
   * to be the V2.3 prototype's palette, whose deep and tint columns were
   * colours defined nowhere in sts-tokens.css (criterion 08.5); it is now a
   * transcription of the [data-sts-type] ramp, so the fallback and the JSON
   * paint the same routine. The neutral set is
   * spec 08 §3.2's, i.e. :root in sts-tokens.css. regimens.css styles
   * everything off these custom properties. */
  function sharedRamp(code) {
    try {
      var el = document.getElementById('sts-skin-types');
      var data = el ? JSON.parse(el.textContent || 'null') : null;
      var entry = data && data[String(code || '').toUpperCase()];
      var ok = function (v) { return typeof v === 'string' && /^#[0-9a-fA-F]{6}$/.test(v); };
      return entry && ok(entry.hex) && ok(entry.deep) && ok(entry.tint) ? entry : null;
    } catch (e) { return null; } // malformed JSON: use the fallback table
  }
  var NEUTRAL = { hex: '#7B85B4', deep: '#2F3572', tint: '#EEF0F8' };
  var TYPE_PALETTE = {
    DSPT: { hex: '#A65E94', deep: '#85377F', tint: '#EFE2EC' },
    DSNT: { hex: '#D88AAE', deep: '#B84E7E', tint: '#F6E8EF' },
    DSPW: { hex: '#CB5D77', deep: '#B0334F', tint: '#F5E4E8' },
    DSNW: { hex: '#DEA796', deep: '#B8654F', tint: '#F8ECE6' },
    OSPT: { hex: '#D68762', deep: '#B4552F', tint: '#F7E8DF' },
    OSNT: { hex: '#EEC8A7', deep: '#B77E4E', tint: '#F9EEE3' },
    OSPW: { hex: '#E2A857', deep: '#A9661F', tint: '#F8EDDB' },
    OSNW: { hex: '#F5DA95', deep: '#A88B3A', tint: '#F9F2DF' },
    ORPT: { hex: '#ABC33F', deep: '#6E8A1E', tint: '#EEF3D9' },
    ORNT: { hex: '#C3D690', deep: '#7A9540', tint: '#EFF3E3' },
    ORPW: { hex: '#86B15A', deep: '#4F7C2C', tint: '#E7F0DD' },
    ORNW: { hex: '#A0C8AB', deep: '#4F8A63', tint: '#E6F1E9' },
    DRPT: { hex: '#6194B0', deep: '#2F6480', tint: '#E1EBF1' },
    DRNT: { hex: '#9CB1D8', deep: '#5871A6', tint: '#E8EDF6' },
    DRPW: { hex: '#5970A3', deep: '#3B4D85', tint: '#E4E7F1' },
    DRNW: { hex: '#A498C1', deep: '#6B5B95', tint: '#ECE9F3' }
  };
  var pal = sharedRamp(skinType) || TYPE_PALETTE[skinType] || NEUTRAL;
  regimensPage.style.setProperty('--slq-type', pal.hex);
  regimensPage.style.setProperty('--slq-type-deep', pal.deep);
  regimensPage.style.setProperty('--slq-type-tint', pal.tint);

  /* Category labels per slot family — the families of
   * snippets/sts-steps-howto.liquid. The family is the letters a slot starts
   * with (C05 -> C, CX21 -> CX, S25T -> S) and is matched EXACTLY: matching by
   * first letter labelled every CX cosmetic a Cleanser, MK masks and MX lip
   * products Moisturizer, SP supplements Sunscreen and TN toners Treatment.
   * A family not listed here gets no label. SPF and EX are older codes, named
   * as the results page (assets/quiz-results.js) names them. */
  var CATEGORY_LABELS = {
    HA: 'Hypoallergenic', RX: 'Prescription', CX: 'Cosmetics', MK: 'Mask',
    MX: 'Lip Care', SP: 'Supplements', TN: 'Toner', PR: 'Prep',
    PP: 'Post-Procedure', PD: 'Pads', H: 'Hair',
    C: 'Cleanser', E: 'Eye Cream', M: 'Moisturizer', S: 'Sunscreen',
    T: 'Treatment', B: 'Body', SPF: 'Sunscreen', EX: 'Exfoliant'
  };
  function categoryForSlot(slot) {
    if (!slot) return null;
    var family = /^[A-Z]+/.exec(String(slot).trim().toUpperCase());
    /* ECS is the regimen templates' unresolved eye placeholder ("Eye Cream
       Specific"): the E family, as in assets/sts-routine-shared.js. */
    var f = family && (family[0] === 'ECS' ? 'E' : family[0]);
    if (!f || !Object.prototype.hasOwnProperty.call(CATEGORY_LABELS, f)) return null;
    return CATEGORY_LABELS[f];
  }

  /* ------------------------------------------------------------------ */
  /* normalisation                                                       */
  /* ------------------------------------------------------------------ */

  /* Never tell someone to apply a product they cannot buy: an out-of-stock
   * step is replaced by its first in-stock, slot-matched swap candidate —
   * the same substitution rule the results page applies. If nothing in the
   * slot is in stock the step renders as-is rather than inventing one. */
  function substituteOutOfStock(step) {
    if (!step || step.inStock !== false) return step;
    /* The comment above said "slot-matched" and the filter never checked the
       slot, so a cleanser could stand in for a sunscreen. It checks now, the
       same way assets/sts-routine-shared.js does. */
    var c = (step.swapCandidates || []).find(function (x) {
      return x && x.dtcVariantId && x.title && x.inStock !== false
        && (!step.slot || !x.slot || (window.STS && STS.sameSlot ? STS.sameSlot(x.slot, step.slot) : String(x.slot) === String(step.slot)));
    });
    if (!c) return step;
    return Object.assign({}, step, {
      title: c.title, brand: c.brand, image: c.image, priceMsrp: c.priceMsrp,
      dtcVariantId: c.dtcVariantId, inStock: c.inStock, handle: c.handle,
      substituted: true,
      /* D32: kept so the row can name what it replaced. */
      substitutedFrom: step.title || null
    });
  }

  var allSteps = rawSteps
    .filter(function (s) { return s && (s.title || s.slot); })
    .map(function (s, i) {
      var step = substituteOutOfStock(Object.assign({}, s));
      /* An empty step titled with the eye placeholder is named by its family. */
      var nt = !step.dtcVariantId && step.title && window.STS && STS.stepLabel ? STS.stepLabel(step, ['title']) : step.title;
      if (nt !== step.title) step.title = nt || categoryForSlot(step.slot) || 'Eye Cream';
      var t = (window.STS && STS.routineTime) ? STS.routineTime(step.time || step.period) : String(step.time || '').toUpperCase();
      step.time = (t === 'AM' || t === 'PM' || t === 'BOTH') ? t : 'BOTH';
      step.key = 'k' + i;
      return step;
    });

  var starterSteps = allSteps.filter(function (s) { return s.tier === 'starter'; });
  // Only offer the toggle when the two sizes actually differ — mirrors the
  // results page, which hides "5+ Step" when they are identical.
  var sizesDiffer = starterSteps.length > 0 && starterSteps.length < allSteps.length;

  var showTypes = ['less', 'full']; // less = Starter, full = Complete
  regimensPage.dataset.productsShow = 'full';

  var byKey = {};
  allSteps.forEach(function (s) { byKey[s.key] = s; });

  var filterByTime = function (steps, time) {
    return steps.filter(function (s) { return s.time === time || s.time === 'BOTH'; });
  };

  /* ------------------------------------------------------------------ */
  /* Shopify storefront lookups                                          */
  /* ------------------------------------------------------------------ */

  var gqlOptions = function (query) {
    return {
      method: 'POST',
      headers: {
        'Content-Type': 'application/graphql',
        'X-Shopify-Storefront-Access-Token': storefrontAccessToken
      },
      body: query
    };
  };

  // One batched query for every product in the routine — powers the modal
  // (live price, availability, description, ingredient + skin-type metafields).
  var queryOfProducts = function (idsJson) {
    return '{ nodes(ids: ' + idsJson + ') { ... on ProductVariant { availableForSale id title price { amount currencyCode } product { id title handle description featuredImage { url altText } ingredients: metafield(namespace: "product_info" key: "ingredients") { value } skinTypes: metafield(namespace: "product_info" key: "baumann_skin_type_info") { value } } } } }';
  };

  var productDetailByVariant = {}; // numeric variant id -> GQL node
  async function loadProductDetails() {
    var gids = allSteps
      .filter(function (s) { return s.dtcVariantId; })
      .map(function (s) { return 'gid://shopify/ProductVariant/' + s.dtcVariantId; });
    if (!gids.length) return;
    try {
      var res = await fetch(GRAPHQL_URL, gqlOptions(queryOfProducts(JSON.stringify(gids))));
      var data = await res.json();
      (data && data.data && data.data.nodes || []).forEach(function (node) {
        if (!node || !node.id) return;
        productDetailByVariant[node.id.split('/').pop()] = node;
      });
    } catch (e) { /* modal falls back to the step's own fields */ }
  }

  // Application instructions live in `product_instruction_steps` metaobjects,
  // one per SLOT (handle = slot code; lookup is case-insensitive). Tinted
  // sunscreen slots (S25T…) have no metaobject of their own — the base slot's
  // instructions apply, so the trailing T is retried without.
  var instructionCache = {};
  var queryInstructionSteps = function (slotId) {
    return '{ metaobject(handle: {handle: "' + slotId + '", type: "product_instruction_steps"}) { id handle fields { key type value } } }';
  };
  async function fetchInstructionSet(slot) {
    if (!slot) return null;
    var key = String(slot).toUpperCase();
    if (key in instructionCache) return instructionCache[key];
    var fields = null;
    try {
      var res = await fetch(GRAPHQL_URL, gqlOptions(queryInstructionSteps(key)));
      var data = await res.json();
      fields = (data && data.data && data.data.metaobject && data.data.metaobject.fields) || null;
      if (!fields && /^S\d+T$/.test(key)) {
        var res2 = await fetch(GRAPHQL_URL, gqlOptions(queryInstructionSteps(key.slice(0, -1))));
        var data2 = await res2.json();
        fields = (data2 && data2.data && data2.data.metaobject && data2.data.metaobject.fields) || null;
      }
    } catch (e) { /* no instructions is fine */ }
    instructionCache[key] = fields;
    return fields;
  }

  var imageUrlCache = {};
  var queryImage = function (id) {
    return '{ node(id: "' + id + '") { ... on MediaImage { image { url altText } } } }';
  };
  async function fetchImageURL(id) {
    if (!id) return null;
    if (id in imageUrlCache) return imageUrlCache[id];
    var image = null;
    try {
      var res = await fetch(GRAPHQL_URL, gqlOptions(queryImage(id)));
      var data = await res.json();
      image = (data && data.data && data.data.node && data.data.node.image) || null;
    } catch (e) { /* placeholder renders instead */ }
    imageUrlCache[id] = image;
    return image;
  }

  /* ------------------------------------------------------------------ */
  /* cart                                                                */
  /* ------------------------------------------------------------------ */

  var openCartWithVariantId = function (variantId) {
    if (!drawer) return;
    drawer.dispatchEvent(new CustomEvent('theme:drawer:open', {
      detail: { variant: variantId, reinit: true },
      bubbles: true
    }));
  };

  var addProductVariantToCart = async function (variantId) {
    try {
      var response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }] })
      });
      var data = await response.json();
      if (data.status === 422) {
        console.error('Error occurred while adding product to cart:', data.description);
        return;
      }
      openCartWithVariantId(variantId);
      body.classList.remove('modal-view-product-open');
    } catch (error) {
      console.error('Error occurred while adding product to cart:', error);
    }
  };

  /* ------------------------------------------------------------------ */
  /* markup builders — original class names, so the old CSS applies      */
  /* ------------------------------------------------------------------ */

  var escapeHtml = function (str) {
    return String(str === null || str === undefined ? '' : str)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;').replace(/'/g, '&#039;');
  };

  var makeImage = function (src, alt, fallback, dataAttribute) {
    return '<img src="' + escapeHtml(src || fallback) + '" alt="' + escapeHtml(alt || defaultAltText) + '" loading="lazy" ' +
      (dataAttribute || '') + ' onerror="this.onerror=null;this.src=\'' + escapeHtml(fallback) + '\';">';
  };

  // The _300x size suffix only makes sense on Shopify CDN .png paths (that is
  // all the old data ever had); anything else renders at its natural URL.
  var sizedImageUrl = function (imageUrl) {
    if (!imageUrl || imageUrl.indexOf('.png') === -1) return imageUrl;
    var split = imageUrl.split('.png');
    return split[0] + '_300x.png' + (split[1] || '');
  };

  var makeProductInstruction = async function (instruction, currentStepImage) {
    var image = await fetchImageURL(currentStepImage && currentStepImage.value);
    return '<div class="swiper-slide"><div class="product-card__instruction">' +
      '<div class="product-card__instruction-image">' + makeImage(image && image.url, instruction.value, defaultImage) + '</div>' +
      '<p>' + escapeHtml(instruction.value) + '</p>' +
      '</div></div>';
  };

  var makeProductInstructions = async function (instructionSet) {
    if (!instructionSet) return '';
    var stepName = /^step_.+_name$/i;
    var stepImage = /^step_.+_image$/i;
    var slides = (await Promise.all(instructionSet.map(async function (instruction) {
      if (!stepName.test(String(instruction.key).toLowerCase())) return '';
      if (!instruction.value) return '';
      var stepNumber = instruction.key.split('_')[1];
      var currentStepImage = instructionSet.find(function (f) {
        return stepImage.test(String(f.key).toLowerCase()) && f.key.indexOf(stepNumber) !== -1;
      });
      return makeProductInstruction(instruction, currentStepImage);
    }))).join('');
    if (!slides) return '';
    return '<div data-swiper-container class="swiper-container" style="overflow: hidden">' +
      '<div class="swiper-pagination" style="position: relative;"></div>' +
      '<div class="swiper-wrapper">' + slides + '</div>' +
      '</div>';
  };

  var makeProductCard = async function (step, index, badge) {
    var hasVariant = !!step.dtcVariantId;
    var imgUrl = sizedImageUrl(step.image) || defaultProductImage;
    var dataAttribute = hasVariant ? 'data-view-product' : '';
    var instructionSet = await fetchInstructionSet(step.slot);
    var productInstructions = await makeProductInstructions(instructionSet);
    var category = categoryForSlot(step.slot);
    var meta = (category || badge)
      ? '<div class="product-card__meta">' +
        (category ? '<span class="product-card__cat">' + escapeHtml(category) + '</span>' : '') +
        (badge ? '<span class="product-card__ampm">' + escapeHtml(badge) + '</span>' : '') +
        '</div>'
      : '';
    return '<div data-show data-key="' + step.key + '" class="product-card">' +
      '<span class="product-card__stepnum">' + (index + 1) + '</span>' +
      '<div ' + (dataAttribute ? dataAttribute + ' style="cursor: pointer"' : '') + ' class="product-card__image">' +
      makeImage(imgUrl, step.title, defaultProductImage, dataAttribute) +
      (dataAttribute ? '<div class="product-card__view"><button class="button-secondary" data-view-product>View</button></div>' : '') +
      '</div>' +
      '<div class="product-card__content">' +
      meta +
      '<h3 class="product-card__title">' + escapeHtml(step.title) + '</h3>' +
      /* D32: an out-of-stock step is replaced, and the replacement is named
         next to what it replaced. This page substituted silently before. */
      (step.substituted && step.substitutedFrom
        ? '<p class="product-card__subnote">' + escapeHtml(step.substitutedFrom)
          + ' is out of stock, so we have put this in its place.</p>'
        : '') +
      (dataAttribute ? '<span class="product-card__hint desktop-hidden">'
        /* The word follows the POINTER, the class follows the VIEWPORT: .desktop-hidden
           only decides whether the hint appears at all (regimens.css hides it at
           >=993px), so without this pair a narrowed desktop window still read "Tap".
           assets/sts-tokens.css:268-269 hides exactly one of the two spans. */
        + '<span class="sts-only-touch">Tap</span><span class="sts-only-pointer">Click</span> product to learn more</span>' : '') +
      '<div class="product-card__instructions">' + productInstructions + '</div>' +
      '</div></div>';
  };

  var makeProductsList = async function (steps, badge) {
    if (!steps.length) return '<h3 class="no-products__title">' + escapeHtml(noProductsError) + '</h3>';
    var cards = (await Promise.all(steps.map(function (step, index) { return makeProductCard(step, index, badge); }))).join('');
    return '<button data-product-cards="prev" aria-label="Previous step" class="product-cards__button left desktop-hidden">&#8249;</button>' +
      '<div class="product-cards">' + cards + '</div>' +
      '<button data-product-cards="next" aria-label="Next step" class="product-cards__button right desktop-hidden">&#8250;</button>';
  };

  var makeHeading = function (label, badge, cssClass) {
    return '<div class="products-container__heading ' + cssClass + '">' +
      '<h2 class="products-container__heading-title">' + label + ' Routine</h2>' +
      '<span class="products-container__heading-badge">' + badge + '</span></div>';
  };

  var makeProgressBar = function (count) {
    if (!count) return '';
    var stepNumbers = Array.from({ length: count }, function (_, i) { return i + 1; });
    var defaultWidth = (100 / count).toFixed(2);
    return '<div class="progress desktop-hidden">' +
      '<div class="progress-bar"><div style="width: ' + defaultWidth + '%" class="progress-bar__line"></div></div>' +
      '<div class="progress-bar__steps">' + stepNumbers.map(function (n) { return '<div class="progress-bar__step">Step ' + n + '</div>'; }).join('') + '</div>' +
      '</div>';
  };

  var makeProductsContainerAndHeading = async function (steps, label, badge, cssClass) {
    var emptyClass = steps.length === 0 ? 'no-products' : '';
    var productsList = await makeProductsList(steps, badge);
    return '<div class="product-time__container" data-current-slide="0">' +
      makeHeading(label, badge, cssClass) + makeProgressBar(steps.length) +
      '<div class="products-container ' + emptyClass + '">' + productsList + '</div>' +
      '</div>';
  };

  var changeRoutineSection = function (currentType) {
    var routineText = currentType === 'full' ? 'Complete' : 'Starter';
    var typeLine = skinType
      ? '<p class="change-routine__type-line" style="text-align:center;font-size:14px;margin:4px 0 0">Built for your Baumann Skin Type&reg; <b>' + escapeHtml(skinType) + '</b> &middot; <a href="' + RESULTS_URL + '">View &amp; shop your full results &rarr;</a></p>'
      : '<p class="change-routine__type-line" style="text-align:center;font-size:14px;margin:4px 0 0"><a href="' + RESULTS_URL + '">View &amp; shop your full results &rarr;</a></p>';
    var toggle = sizesDiffer
      ? '<div class="add-routine__type"><span>Complete Routine</span>' +
        '<div data-routine-type-change class="add-routine__cta"><span data-routine-type-change></span></div>' +
        '<span>Starter Routine</span></div>'
      : '';
    return '<div class="change-routine">' +
      '<h5 class="change-routine__title">How To Apply Your ' + routineText + ' Routine</h5>' +
      typeLine + toggle +
      '</div>';
  };

  var makeViewProductModal = function (step) {
    var node = productDetailByVariant[step.dtcVariantId];
    var product = node && node.product;
    var title = (product && product.title) || step.title || '';
    var priceAmount = node && node.price ? Number(node.price.amount) : (step.priceMsrp !== null && step.priceMsrp !== undefined ? Number(step.priceMsrp) : null);
    var description = (product && product.description) || '';
    var imgSrc = sizedImageUrl((product && product.featuredImage && product.featuredImage.url) || step.image) || defaultProductImage;
    var ingredientsArr = product && product.ingredients && product.ingredients.value
      ? product.ingredients.value.split(',') : null;
    var skinTypesArr = null;
    try {
      skinTypesArr = product && product.skinTypes && product.skinTypes.value ? JSON.parse(product.skinTypes.value) : null;
    } catch (e) { skinTypesArr = null; }
    var available = node ? node.availableForSale !== false : step.inStock !== false;
    var keyIngredientsImgUrl = savedValues.keyIngredientsImgUrl || '';

    return '<div class="modal-view__close"><button data-view-product="close" class="modal-view__close-button">x</button></div>' +
      '<div class="modal-view__content">' +
      '<div class="modal-view__content-product">' +
      '<div class="modal-view__content-product-image">' + makeImage(imgSrc, title, defaultImage) + '</div>' +
      '<div class="modal-view__content-product-info">' +
      '<h5 class="modal-view__content-product-title">' + escapeHtml(title) + '</h5>' +
      (priceAmount !== null && isFinite(priceAmount) ? '<p class="modal-view__content-product-price">$' + priceAmount.toFixed(2) + '</p>' : '') +
      '<div class="modal-view_button">' +
      (available
        ? '<button data-variant-id="' + escapeHtml(step.dtcVariantId) + '" class="button-primary">Add to cart</button>'
        : '<button class="button-primary" disabled>Out of stock</button>') +
      '</div></div></div>' +
      '<div class="modal-view__content-content">' +
      (description ? '<div class="modal-view__content-desc"><p>' + escapeHtml(description) + '</p></div>' : '') +
      (ingredientsArr ? '<div class="modal-view__ingredients"><h5 class="modal-view__ingredients-title">Full ingredient list</h5>' +
        '<div class="modal-view__ingredients-container">' + ingredientsArr.map(function (i) { return '<span>' + escapeHtml(i) + '</span>'; }).join(', ') + '</div></div>' : '') +
      (skinTypesArr && skinTypesArr.length ? '<div class="modal-view__recomended"><h5 class="modal-view__recomended-title">Recommended for Skin Types</h5>' +
        '<div class="modal-view__recomended-container">' + skinTypesArr.map(function (t) {
          return '<div class="modal-view__recomended-item">' +
            '<span class="modal-view__recomended-item-icon">' + makeImage(keyIngredientsImgUrl + t + '.png', t, defaultImage) + '</span>' +
            '<span class="modal-view__recomended-item-text">' + escapeHtml(t) + '</span></div>';
        }).join('') + '</div></div>' : '') +
      '</div>';
  };

  /* ------------------------------------------------------------------ */
  /* render + interactions                                               */
  /* ------------------------------------------------------------------ */

  var initSwipers = function () {
    if (typeof Swiper === 'undefined') return;
    document.querySelectorAll('[data-swiper-container]').forEach(function (el) {
      new Swiper(el, {
        slidesPerView: 1,
        spaceBetween: 20,
        pagination: { el: el.querySelector('.swiper-pagination'), clickable: true }
      });
    });
  };

  var currentSteps = function () {
    return regimensPage.dataset.productsShow === 'less' ? starterSteps : allSteps;
  };

  var renderRoutineSections = async function () {
    var steps = currentSteps();
    var sections = await Promise.all([
      makeProductsContainerAndHeading(filterByTime(steps, 'AM'), 'Morning', 'AM', 'morning'),
      makeProductsContainerAndHeading(filterByTime(steps, 'PM'), 'Evening', 'PM', 'evening')
    ]);
    return sections.join('');
  };

  toggleLoader(true);
  await loadProductDetails();
  var initialSections = await renderRoutineSections();
  toggleLoader(false);

  regimensPage.innerHTML =
    changeRoutineSection(regimensPage.dataset.productsShow) +
    '<div class="product-container">' + initialSections + '</div>' +
    '<div class="modal-view-product">' +
    '<div data-view-product="close" class="modal-view__overlay"></div>' +
    '<div data-modal-view-product-container class="modal-view-product-container"></div>' +
    '</div>';
  initSwipers();

  regimensPage.addEventListener('click', function (e) {
    var target = e.target;

    if (target.matches('[data-view-product]')) {
      if (target.dataset.viewProduct === 'close') {
        body.classList.remove('modal-view-product-open');
        return;
      }
      var productCard = target.closest('.product-card[data-show]');
      if (!productCard) return;
      var step = byKey[productCard.getAttribute('data-key')];
      if (!step || !step.dtcVariantId) return;
      var productViewModal = document.querySelector('[data-modal-view-product-container]');
      productViewModal.innerHTML = makeViewProductModal(step);
      body.classList.toggle('modal-view-product-open');

    } else if (target.matches('[data-routine-type-change]')) {
      var currentType = regimensPage.dataset.productsShow;
      currentType = showTypes.filter(function (t) { return t !== currentType; })[0];
      regimensPage.setAttribute('data-products-show', currentType);
      var heading = target.closest('.change-routine').querySelector('.change-routine__title');
      heading.textContent = 'How To Apply Your ' + (currentType === 'full' ? 'Complete' : 'Starter') + ' Routine';
      var productsContainers = regimensPage.querySelector('.product-container');
      renderRoutineSections().then(function (html) {
        productsContainers.innerHTML = html;
        initSwipers();
      });

    } else if (target.matches('[data-product-cards]')) {
      var dir = target.dataset.productCards;
      var currentParent = target.closest('.product-time__container');
      var currentSlide = +currentParent.getAttribute('data-current-slide');
      var currentProgress = currentParent.querySelector('.progress-bar__line');
      var currentContainer = currentParent.querySelector('.product-cards');
      var cardsCount = currentParent.querySelectorAll('.product-card').length;
      if (dir === 'prev') {
        if (currentSlide === 0) return;
        currentSlide--;
      } else {
        if (currentSlide === cardsCount - 1) return;
        currentSlide++;
      }
      currentContainer.style.transform = 'translateX(-' + (currentSlide * 100) + '%)';
      currentParent.setAttribute('data-current-slide', currentSlide);
      if (currentProgress) currentProgress.style.width = ((currentSlide + 1) * 100 / cardsCount) + '%';

    } else if (target.matches('[data-variant-id]')) {
      addProductVariantToCart(target.getAttribute('data-variant-id'));
    }
  });
})();
